import { Receipt } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import CostCalculator from "./CostCalculator";
import DeliveryInfoButton from "./DeliveryInfoButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ColoredOutlineButton, OUTLINE_ICON_BUTTON_ICON_CLASS } from "@/components/ui/colored-outline-button";
import { DeliveryFeeZones } from "@/types/listing";

interface FeesAndDeliveryBlockProps {
  price: number;
  deliveryFee?: DeliveryFeeZones;
  /** When true, nothing is rendered (e.g. seller's own listing). */
  hideWhenOwn?: boolean;
  className?: string;
  /** Pickup line shown under “Upphämtning” (e.g. seller’s city). */
  pickupDisplayText?: string | null;
}

/**
 * Avgifter (CostCalculator popover/sheet) + Leverans (DeliveryInfoButton).
 * Used in both auction (TimeRemaining) and direct purchase (BiddingInfo) views.
 */
const FeesAndDeliveryBlock = ({
  price,
  deliveryFee,
  hideWhenOwn = false,
  className,
  pickupDisplayText,
}: FeesAndDeliveryBlockProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language).bidding;
  const pickupLine =
    pickupDisplayText?.trim() ||
    carDetailsLabels(language).delivery.pickupCityUnavailable;
  const [feesOpen, setFeesOpen] = useState(false);

  if (hideWhenOwn) {
    return null;
  }

  return (
    <>
      <div className={className ?? "flex gap-1.5 flex-shrink-0"}>
        {isMobile ? (
          <ColoredOutlineButton
            type="button"
            onClick={() => setFeesOpen(true)}
            size="icon"
            variant="neutral"
          >
            <Receipt className={OUTLINE_ICON_BUTTON_ICON_CLASS} strokeWidth={2.35} />
          </ColoredOutlineButton>
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <div>
                <ColoredOutlineButton type="button" size="icon" variant="neutral">
                  <Receipt className={OUTLINE_ICON_BUTTON_ICON_CLASS} strokeWidth={2.35} />
                </ColoredOutlineButton>
              </div>
            </PopoverTrigger>
            <PopoverContent
              className={`z-[220] w-72 p-4 ${
                isDark
                  ? "bg-gray-900 text-white border-gray-700"
                  : "bg-white text-black border-gray-200"
              }`}
            >
              <h3
                className={`font-medium mb-3`}
              >
                {labels.fees}
              </h3>
              <CostCalculator price={price} />
            </PopoverContent>
          </Popover>
        )}

        <DeliveryInfoButton deliveryFee={deliveryFee} location={pickupLine} />
      </div>

      <Sheet open={feesOpen} onOpenChange={setFeesOpen}>
        <SheetContent
          side="bottom"
          nestedSheet
          overlayClassName="!z-[230]"
          className={`${
            isDark
              ? "bg-gray-900/80 backdrop-blur-md text-white border-gray-700"
              : "bg-white/90 backdrop-blur-md text-black border-gray-200"
          } !z-[231] rounded-t-xl`}
        >
          <SheetHeader>
            <SheetTitle
              className={`text-center text-base sm:text-lg`}
            >
              {labels.fees}
            </SheetTitle>
          </SheetHeader>
          <div className="py-4">
            <CostCalculator price={price} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default FeesAndDeliveryBlock;
